import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import _ from 'lodash';
import { toast } from 'react-semantic-toasts';
import { REPROCESS_VIDEO } from '../../../../../core/graphql/test-sessions';

import { config } from '../../../../../config';
import { COLUMNS, TOAST_TIME } from '../../constants';

import * as S from './styles';
import { TEST_SESSION_LIST } from '../../../../../core/hooks/use-sessions/graphql';

const { REACT_APP_COMPANY_PLATFORM_URI } = config;

function SessionsTable({ data }) {
  const [currentReprocessSessionIds, setCurrentReprocessSessionIds] = useState([]);

  const [
    reprocessSession,
    { loading: isSendingToReprocess },
  ] = useMutation(REPROCESS_VIDEO, {
    update: (cache, { data: response }) => {
      const { reprocessVideo } = response;
      const { testSessionList } = cache.readQuery({ query: TEST_SESSION_LIST, variables: { status: 'transcribe_error' } });

      cache.writeQuery({
        query: TEST_SESSION_LIST,
        data: {
          testSessionList: testSessionList.map((session) => {
            if (session.id === reprocessVideo.testSession.id) {
              return {
                ...session,
                status: 'processing',
              };
            }

            return session;
          }),
        },
      });
    },
  });

  const handleCopy = (value, description) => {
    if (value) {
      copy(value);
      toast({
        description: `${description} copied to the clipboard!`,
        time: TOAST_TIME,
        type: 'success',
      });

      return;
    }

    toast({
      description: 'There was an error copying the value to the clipboard',
      time: TOAST_TIME,
      type: 'error',
    });
  };

  const handleReprocessSession = async (testSessionId) => {
    try {
      setCurrentReprocessSessionIds((state) => [...state, testSessionId]);
      const response = await reprocessSession({ variables: { testSessionId } });

      if (!response || response.error) throw new Error('There was an error reprocessing the session');

      toast({
        description: `The session ${testSessionId} was sent to reprocessing`,
        time: TOAST_TIME,
        type: 'success',
      });
    } catch (error) {
      toast({
        description: 'There was an error reprocessing the session',
        time: TOAST_TIME,
        type: 'error',
      });
    } finally {
      setCurrentReprocessSessionIds((state) => state.filter((id) => id !== testSessionId));
    }
  };

  const renderHeaders = useCallback(() => Object.entries(COLUMNS).map(([key, value]) => (
    <S.HeaderColumn key={key}>
      <S.Text.Bold>{value}</S.Text.Bold>
    </S.HeaderColumn>
  )), []);

  const projectLink = (projectId) => `${REACT_APP_COMPANY_PLATFORM_URI}/projects/${projectId}/study`;
  const projectName = useCallback((session) => {
    const { name, id: projectId } = _.result(session, 'tester.project');

    return (
      <S.CellWrapper>
        <S.Text>
          {name}
          <S.Icon
            name="external"
            onClick={() => {
              window.open(projectLink(projectId), '_blank', 'noopener,noreferrer');
            }}
          />
        </S.Text>
      </S.CellWrapper>
    );
  }, []);

  const reprocessButton = useCallback((session) => {
    const { id: testSessionId, status } = session;
    const isLoading = isSendingToReprocess && currentReprocessSessionIds.includes(testSessionId);

    if (status === 'transcribe_error') {
      return (
        <S.ReprocessButton
          onClick={() => handleReprocessSession(testSessionId)}
          disabled={isLoading}
          loading={isLoading}
        >
          Reprocess
        </S.ReprocessButton>
      );
    }

    return undefined;
  }, [handleReprocessSession]);

  const testerName = useCallback((session) => {
    const sessionLink = `${REACT_APP_COMPANY_PLATFORM_URI}/projects/${session.tester.project.id}/videos/${session.tester.id}`;

    return (
      <S.CellWrapper>
        <S.Text>
          {session.fullName}
          <S.Icon
            name="external"
            onClick={() => {
              window.open(sessionLink, '_blank', 'noopener,noreferrer');
            }}
          />
        </S.Text>
      </S.CellWrapper>
    );
  }, []);

  const tableBody = useMemo(() => data.map((session) => (
    <S.Row key={session.id}>
      <S.Column><S.CellWrapper><S.Text>{session.companyName}</S.Text></S.CellWrapper></S.Column>
      <S.Column>{projectName(session)}</S.Column>
      <S.Column>{testerName(session)}</S.Column>
      <S.Column>
        <S.CellWrapper>
          <S.Text
            style={{ cursor: 'pointer' }}
            onClick={() => handleCopy(session.id, 'Test session ID')}
          >
            {session.id}
          </S.Text>
        </S.CellWrapper>
      </S.Column>
      <S.Column><S.CellWrapper><S.Text>{session.status}</S.Text></S.CellWrapper></S.Column>
      <S.Column><S.CellWrapper><S.Text>{reprocessButton(session)}</S.Text></S.CellWrapper></S.Column>
    </S.Row>
  )), [data, projectName, testerName, reprocessButton]);

  return (
    <S.Container>
      <S.Table basic="very">
        <S.Header>
          <S.Row>
            {renderHeaders()}
          </S.Row>
        </S.Header>

        <S.Body>
          {tableBody}
        </S.Body>
      </S.Table>
    </S.Container>
  );
}

SessionsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default SessionsTable;
